import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Printer, XCircle } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import { Participant, Vehicle, won } from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow } from '../types/cashflow';
import { computeMetrics, mult, pct } from '../lib/metrics';
import { capitalRows, ledgerBalance } from '../lib/capital';
import { fundInfo } from '../lib/fund';
import { feeRows as calcFeeRows } from '../lib/fee';
import { http } from '../services/http';
import { Employee, employeeApi, nameOf } from '../services/employeeApi';
import { Tour, TourButton, TourStep } from '../components/Tour';

/**
 * LP(출자자) 정기보고 — 영업보고서 6장 양식.
 *
 * 양식은 factsheet 공개 데모(factsheetdemo.vercel.app, 2026-10-09 확인)의 영업보고서와 같은 순서다 —
 * ① 펀드 현황 ② 추가 납입 계획 ③ 조합자산현황 ④ 조합평가현황 ⑤ 투자자산현황 ⑥ 투자기업 세부현황.
 * 숫자는 전부 원장 · 평가액에서 그 기간 말까지 잘라 계산한다. 맨 위에 정합성 자가검증을 둔다.
 *
 * ⚠️ 규약 값(대표 펀드매니저 · 보수율 등)은 자금원 행(`investment_vehicles`) — 확인 전 자금원은 「규약 확인 전」으로 비운다.
 */
const PERIODS = ['2026-H1', '2025-FY', '2026-Q3', '2026-Q2'];
const periodEnd = (p: string) => (p.endsWith('FY') ? `${p.slice(0, 4)}-12-31` : p.endsWith('H1') ? `${p.slice(0, 4)}-06-30`
  : `${p.slice(0, 4)}-${String(Number(p.slice(-1)) * 3).padStart(2, '0')}-${['Q1', 'Q4'].includes(p.slice(-2)) ? '31' : '30'}`);
const periodStart = (p: string) => (p.endsWith('FY') ? `${p.slice(0, 4)}-01-01` : p.endsWith('H1') ? `${p.slice(0, 4)}-01-01`
  : `${p.slice(0, 4)}-${String(Number(p.slice(-1)) * 3 - 2).padStart(2, '0')}-01`);
const periodLabel = (p: string) => (p.endsWith('FY') ? `${p.slice(0, 4)}년 온기` : p.endsWith('H1') ? `${p.slice(0, 4)}년 반기` : `${p.slice(0, 4)}년 ${p.slice(-1)}분기`);
const M = (v: number) => (v === 0 ? '-' : (v / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 })); // 백만원

const H: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => <h3 className="text-sm font-bold text-slate-900 mt-1 mb-2">{n}. {children}</h3>;
const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => <h4 className="text-xs font-semibold text-slate-600 mt-3 mb-1">◎ {children}</h4>;
const T: React.FC<{ head: string[]; rows: (string | number)[][]; right?: number[] }> = ({ head, rows, right = [] }) => (
  <table className="w-full text-sm border-t border-slate-300">
    <thead><tr className="text-xs text-slate-500 border-b border-slate-200">{head.map((h, i) => <th key={h} className={`font-medium py-1.5 ${right.includes(i) ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-100">{r.map((c, j) => <td key={j} className={`py-1.5 ${right.includes(j) ? 'text-right tabular-nums' : ''}`}>{c}</td>)}</tr>)}</tbody>
  </table>
);

interface Holder { companyId: number; asOf: string; vehicleId: number | null; shares: number }
interface Fin { companyId: number; fiscalYear: number; totalAssets: number | null; totalLiabilities: number | null; totalEquity: number | null; revenue: number | null; operatingIncome: number | null; netIncome: number | null }

export const LpReportPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [parts, setParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vals, setVals] = useState<Valuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [vid, setVid] = useState<number | null>(null);
  const [period, setPeriod] = useState(PERIODS[0]);
  const [tourRun, setTourRun] = useState(false);
  const [holders, setHolders] = useState<Holder[]>([]);
  const [fins, setFins] = useState<Fin[]>([]);
  const [emps, setEmps] = useState<Employee[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p, d, c, f, va, sh, fi, em] = await Promise.all([vehicleApi.list(), vehicleApi.allParticipants(), dealApi.list(), companyApi.list(), cashflowApi.list(), dealApi.valuations(),
        http.get<Holder[]>('/shareholders'), http.get<Fin[]>('/financials', { isForecast: 'false', fiscalPeriod: 'FY' }), employeeApi.list().catch(() => [])]);
      setVehicles(v); setParts(p); setDeals(d); setCompanies(c); setFlows(f); setVals(va); setFins(fi); setEmps(em);
      // 주주명부는 받은 날짜별 스냅샷 — 회사마다 가장 최근 것만
      const lastAsOf: Record<number, string> = {};
      sh.forEach((x) => { if (!lastAsOf[x.companyId] || x.asOf > lastAsOf[x.companyId]) lastAsOf[x.companyId] = x.asOf; });
      setHolders(sh.filter((x) => x.asOf === lastAsOf[x.companyId]));
      setVid((cur) => cur ?? v.find((x) => x.legalForm === '조합')?.id ?? v.find((x) => x.kind === 'entrusted')?.id ?? null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const v = vehicles.find((x) => x.id === vid);
  const end = periodEnd(period);
  const start = periodStart(period);
  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';
  const info = fundInfo(v);
  const meta = v;
  const lead = v ? nameOf(emps, v.leadManagerEmployeeId) : null;
  const keyPeople = v && v.keyPersonEmployeeIds?.length ? v.keyPersonEmployeeIds.map((id) => nameOf(emps, id)).join(', ') : null;

  const r = useMemo(() => {
    if (!v) return null;
    const upto = flows.filter((f) => f.vehicleId === v.id && f.occurredOn <= end);
    const inP = upto.filter((f) => f.occurredOn >= start);
    const sum = (xs: Cashflow[], k: string) => xs.filter((f) => f.kind === k).reduce((s, f) => s + f.amount, 0);
    const myParts = parts.filter((p) => p.vehicleId === v.id);
    const cap = capitalRows(myParts, upto);
    const committed = myParts.reduce((s, p) => s + (p.committedAmount ?? 0), 0);
    const paid = sum(upto, 'contribution');
    const myDeals = deals.filter((d) => d.vehicleId === v.id && d.investedOn && d.investedOn <= end);
    const latest = (dealId: number) => vals.filter((x) => x.dealId === dealId && x.valuedOn <= end).sort((a, b) => b.valuedOn.localeCompare(a.valuedOn))[0];
    const holdings = myDeals.map((d) => {
      const inv = upto.filter((f) => f.dealId === d.id && f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
      // 끝난 건은 잔여가치 0 — 재원 상세 · 대시보드 · 내부보고와 같은 규칙(회수 · 상각).
      // 회수는 그 기간 안에 회수 원장이 있을 때만, 상각은 날짜를 따로 적지 않아 지금 상태로 본다
      const exited = (d.stage === '회수' && upto.some((f) => f.dealId === d.id && f.kind === 'exit')) || d.stage === '상각';
      const val = exited ? 0 : (latest(d.id)?.ourValue ?? null);
      const holder = holders.find((s) => s.companyId === d.companyId && s.vehicleId === v.id);
      const total = holders.filter((s) => s.companyId === d.companyId).reduce((s, x) => s + x.shares, 0);
      return { d, inv, exited, val, share: holder && total && holder.shares ? holder.shares / total : null };
    });
    const held = holdings.filter((h) => !h.exited);
    const a = held.reduce((s, h) => s + (h.val ?? h.inv), 0);           // 투자자산 — 평가 없으면 취득원가
    const b = ledgerBalance(upto);                                       // 미투자자산 = 통장 잔액
    const c = 0;                                                         // 부채 — 아직 기록하지 않음
    const dist = sum(upto, 'distribution');
    const e = a + b - c;
    // 평가 없는 보유 건은 투자자산(a)과 같이 취득원가로 — TVPI · IRR 도 a 를 쓴다. 기준일은 기간 말일
    const m = computeMetrics(upto, a, new Date(end));
    // 보수 — 원장의 보수 구분(fee_kind)으로 관리 · 성과를 가른다
    const isCarry = (f: Cashflow) => f.feeKind === 'carry';
    // 관리보수 — 규약대로 날마다 기준을 정해 일할로 산출하고 지급액과 대조한다(lib/fee.ts).
    // 투자기간 뒤 기준이 바뀌거나(투자잔액) 선취가 있는 조합도 같은 식으로 본다
    const byPaid = v.mgmtFeeBase === 'paid_in';
    const feeBase = byPaid ? paid : committed;
    const yearFee = info ? feeBase * info.mgmtFeeRate : null;
    const ends = myDeals.map((d) => {
      const own = upto.filter((f) => f.dealId === d.id);
      const last = own.reduce((m, f) => (f.occurredOn > m ? f.occurredOn : m), d.investedOn ?? '');
      const lastExit = own.filter((f) => f.kind === 'exit').reduce((m, f) => (f.occurredOn > m ? f.occurredOn : m), '');
      return { dealId: d.id, endedOn: d.stage === '회수' && lastExit ? lastExit : d.stage === '상각' ? last : null };
    });
    const allMgmt = upto.filter((f) => f.kind === 'fee' && !isCarry(f));
    const feeRows = !info ? [] : calcFeeRows(allMgmt, {
      rate: info.mgmtFeeRate, base: v.mgmtFeeBase ?? 'committed', baseAfter: v.mgmtFeeBaseAfter,
      // 결성일 확인 전이면 등록일 · 운용 개시일부터 센다(2026-11-02 — 라이프사이클 · 존속기간과 같은 규칙)
      investEndOn: v.investEndOn, prepaidYears: v.mgmtFeePrepaidYears, formedOn: meta?.formedOn ?? meta?.registeredOn ?? v.startedOn ?? null, committed,
    }, upto, ends).filter((x) => x.f.occurredOn >= start).map((x) => ({ ...x, d: x.days }));
    const carryRows = inP.filter((f) => f.kind === 'fee' && isCarry(f));
    // 정합성 자가검증
    const checks = [
      { ok: paid === cap.reduce((s, x) => s + x.paidIn, 0), text: `납입총액 ${M(paid)} = 조합원별 납입 합계` },
      { ok: holdings.reduce((s, h) => s + h.inv, 0) === sum(upto, 'investment'), text: `투자 건 원금 합계 = 원장 투자 합계 ${M(sum(upto, 'investment'))}` },
      { ok: held.every((h) => h.val !== null), text: `평가 결측 ${held.filter((h) => h.val === null).length}건` },
      { ok: b >= 0, text: `미투자자산(통장 잔액) ${M(b)} ≥ 0` },
      { ok: feeRows.every((x) => x.ok), text: `관리보수 지급액 = 규약 산출액 (${feeRows.filter((x) => !x.ok).length}건 다름)` },
    ];

    // 업무집행조합원 — 공식 문서라 참여자에 넣은 GP 이름(법인명)을 쓴다
    const gpName = myParts.filter((p) => p.isManager).map((p) => p.name).join(' · ') || null;
    const unvaluedN = held.filter((h) => h.val === null).length;
    return { upto, inP, cap, committed, paid, holdings, held, a, b, c, dist, e, m, checks, feeRows, carryRows, yearFee, feeBase, byPaid, sum, gpName, unvaluedN };
  }, [holders, v, flows, deals, vals, parts, end, start, info, meta]);

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'pick', target: '[data-tour="pick"]', title: '조합과 기간', content: '반기 · 온기 · 분기를 고르면 그 기간 말까지의 원장으로 6장 영업보고서를 만듭니다.' },
    { name: 'check', target: '[data-tour="check"]', title: '정합성 자가검증', content: '납입 합계 · 투자잔액 합계 · 평가 결측 · 통장 잔액을 먼저 점검합니다. 하나라도 틀리면 보내기 전에 고칩니다.' },
    { name: 'paper', target: '[data-tour="paper"]', title: '영업보고서', content: '① 펀드 현황 ② 추가 납입 계획 ③ 조합자산현황 ④ 조합평가현황 ⑤ 투자자산현황 ⑥ 투자기업 세부현황. 단위는 백만원입니다.', placement: 'top' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5 print:hidden">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">LP 보고 <TourButton onClick={() => setTourRun(true)} /></h1>
        <div data-tour="pick" className="flex items-center gap-2">
          <select aria-label="자금원" value={vid ?? ''} onChange={(e) => setVid(Number(e.target.value))} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
            {vehicles.filter((x) => x.kind === 'entrusted').map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <select aria-label="기간" value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
            {PERIODS.map((x) => <option key={x} value={x}>{periodLabel(x)}</option>)}
          </select>
          <button onClick={() => window.print()} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700"><Printer size={12} /> 인쇄 · PDF</button>
        </div>
      </div>

      {v && r && (
        <>
          <section data-tour="check" className="mb-4 bg-white border border-slate-200 rounded-lg px-4 py-3 print:hidden">
            <h2 className="text-sm font-semibold text-slate-900 mb-1.5">정합성 자가검증</h2>
            <ul className="grid sm:grid-cols-2 gap-1">{r.checks.map((c) => (
              <li key={c.text} className="flex items-center gap-1.5 text-sm">{c.ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-red-500" />}{c.text}</li>
            ))}</ul>
          </section>

          <article data-tour="paper" className="bg-white border border-slate-200 rounded-lg px-10 py-9 space-y-6 print:border-0 print:px-0 text-slate-800">
            <header className="border-b-2 border-slate-900 pb-3 text-center">
              <h2 className="text-2xl font-bold text-slate-900">영업보고서 ({end.replace(/-/g, '.')} 기준)</h2>
              <div className="text-sm mt-1">{meta?.vicsCodeName ?? v.name}</div>
              <div className="text-xs text-slate-500">업무집행조합원: {r.gpName ?? '(주)지알케이파트너스'} · {periodLabel(period)} · 단위 백만원</div>
            </header>

            <section>
              <H n={1}>펀드 현황</H>
              <Sub>조합 개요</Sub>
              <dl className="grid grid-cols-[140px_1fr] sm:grid-cols-[140px_1fr_140px_1fr] gap-x-3 gap-y-1 text-sm">
                {[
                  ['조합명', meta?.vicsCodeName ?? v.name], ['업무집행조합원', r.gpName ?? '(주)지알케이파트너스'],
                  ['결산기준일', end.replace(/-/g, '.')], ['납입방법', info?.paymentMethod ?? '규약 확인 전'],
                  ['존속기간', (() => {
                    // 결성일 확인 전이면 등록일부터
                    const s0 = meta?.formedOn ?? meta?.registeredOn ?? null;
                    return s0 && meta?.maturityOn ? `${s0.replace(/-/g, '.')} ~ ${meta.maturityOn.replace(/-/g, '.')}` : '규약 확인 전';
                  })()],
                  // 규약 값은 넣었는데 투자기간만 비었으면 규약에 조항이 없는 것(1호처럼)
                  ['투자기간', meta?.investEndOn ? `~ ${meta.investEndOn.replace(/-/g, '.')}` : info && meta?.maturityOn ? '규약에 따로 없음' : '규약 확인 전'],
                  ['약정총액', M(r.committed)], ['납입총액', M(r.paid)],
                  ['대표펀드매니저', lead ?? '규약 확인 전'], ['핵심운용인력', keyPeople ?? '규약 확인 전'],
                  ['투자기구형태', info?.vehicleType ?? v.legalForm ?? '확인 전'], ['우선손실충당순서', info?.lossOrder ?? '규약 확인 전'],
                ].map(([k, val]) => <React.Fragment key={k}><dt className="text-slate-500">{k}</dt><dd>{val}</dd></React.Fragment>)}
              </dl>
              <Sub>조합원 구성 및 변경</Sub>
              <T head={['유형', '조합원', '약정액', '약정비율', '납입액', '분배 누적']} right={[2, 3, 4, 5]}
                rows={[...r.cap.map((c) => [c.p.isManager ? '업무집행' : '유한책임', c.p.name, M(c.committed ?? 0), r.committed ? `${(((c.committed ?? 0) / r.committed) * 100).toFixed(2)}%` : '-', M(c.paidIn), M(c.distributed)]),
                  ['합계', '', M(r.committed), '100%', M(r.paid), M(r.dist)]]} />
              <p className="text-xs text-slate-500 mt-1">분배액에는 원금 반환분이 포함됩니다. 출자잔액은 규약의 분배 순서를 확인한 뒤 표시합니다.</p>
              <Sub>조합 주요 조건</Sub>
              {!info ? <p className="text-sm text-slate-500">규약 확인 전</p> : (
                <T head={['항목', '내용']} rows={[
                  ['기준수익률', `연 ${(info.hurdle * 100).toFixed(0)}%`],
                  ['관리보수', info.mgmtFeeText],
                  ['성과보수', `기준수익률 초과 시 투자수익의 ${(info.carryRate * 100).toFixed(0)}%`],
                ]} />
              )}
              <Sub>회계감사인</Sub>
              <p className="text-sm">{info?.auditor ?? '규약 확인 전'}</p>
            </section>

            <section>
              <H n={2}>향후 1년간 추가 납입요청 계획</H>
              <p className="text-sm">{r.committed - r.paid > 0 ? `미납 약정 ${M(r.committed - r.paid)}` : '해당 없음 (약정 전액 납입)'}</p>
            </section>

            <section>
              <H n={3}>조합자산현황</H>
              <Sub>기간 수익 · 비용</Sub>
              <T head={['구분', '회수(처분)', '배당 · 이자', '관리 · 성과보수', '기타 비용']} right={[1, 2, 3, 4]}
                rows={[[periodLabel(period), M(r.sum(r.inP, 'exit')), M(r.sum(r.inP, 'dividend')), M(r.sum(r.inP, 'fee')), M(r.sum(r.inP, 'expense'))]]} />
              <Sub>관리보수 산출 및 지급내역</Sub>
              {!info ? <p className="text-sm text-slate-500">보수율 규약 확인 전</p> : (
                <>
                  <p className="text-sm">산출식: 그날의 기준액 × {(info.mgmtFeeRate * 100).toFixed(1)}% × 일수 / 365 · {info.mgmtFeeText}</p>
                  {r.feeRows.length === 0 ? <p className="text-sm text-slate-500">기간 내 지급 없음</p> : (
                    <T head={['지급일', '산출 기간', '산출액', '지급액', '대조']} right={[2, 3]} rows={r.feeRows.map((x) => [x.f.occurredOn.replace(/-/g, '.'),
                      x.prepaid ? `선취 ${x.from.replace(/-/g, '.')}부터 ${v.mgmtFeePrepaidYears}년` : `${x.from.replace(/-/g, '.')}부터 ${x.d}일`, x.expected === null ? '-' : won(x.expected), won(x.f.amount),
                      x.ok ? '일치' : `확인 필요 — ${x.expected ? (x.f.amount / x.expected).toFixed(2) : '-'}배`])} />
                  )}
                  <Sub>성과보수 지급내역</Sub>
                  {r.carryRows.length === 0 ? <p className="text-sm">해당사항 없음</p> : (
                    <T head={['지급일', '지급액', '메모']} right={[1]} rows={r.carryRows.map((f) => [f.occurredOn.replace(/-/g, '.'), won(f.amount), f.memo])} />
                  )}
                </>
              )}
              <Sub>미투자자산 운용</Sub>
              <p className="text-sm">조합 통장 잔액 {M(r.b)} (원장 기준)</p>
            </section>

            <section>
              <H n={4}>조합평가현황</H>
              <T head={['투자자산(a)', '미투자자산(b)', '부채(c)', '합계(e=a+b−c)', '납입(f)', '비율(e/f)']} right={[0, 1, 2, 3, 4, 5]}
                rows={[[M(r.a), M(r.b), M(r.c), M(r.e), M(r.paid), r.paid ? `${((r.e / r.paid) * 100).toFixed(2)}%` : '-']]} />
              <p className="text-xs text-slate-500 mt-1">
                누적 분배 {M(r.dist)}은 통장에서 나가 b 에 이미 반영 · TVPI {mult(r.m.tvpi)} · IRR {r.unvaluedN ? '—' : pct(r.m.irr)}
                {r.unvaluedN > 0 && <span className="text-amber-700"> · 평가 없는 {r.unvaluedN}건은 취득원가로 계산 — IRR 은 평가 뒤에</span>}
              </p>
              <Sub>투자자산 평가</Sub>
              <T head={['투자기업', '투자유형', '투자잔액', '평가금액', '증감배수']} right={[2, 3, 4]}
                rows={r.held.map((h) => [cName(h.d.companyId), h.d.securityKind ?? '-', M(h.inv), h.val === null ? '평가 없음' : M(h.val), h.val === null ? '-' : mult(h.val / h.inv)])} />
            </section>

            <section>
              <H n={5}>투자자산현황</H>
              <T head={['투자기업', '최초투자일', '투자형태', '투자금액', '지분율', '투자잔액']} right={[3, 4, 5]}
                rows={r.holdings.map((h) => [cName(h.d.companyId), h.d.investedOn?.replace(/-/g, '.') ?? '-', h.d.securityKind ?? '-', M(h.inv), h.share === null ? (h.d.securityKind === 'SAFE' ? '전환 전' : '-') : `${(h.share * 100).toFixed(2)}%`, h.exited ? (h.d.stage === '상각' ? '상각' : '회수 완료') : M(h.inv)])} />
            </section>

            <section>
              <H n={6}>투자기업 세부현황</H>
              {r.held.map((h) => {
                const p = companies.find((x) => x.id === h.d.companyId);
                const fin = fins.filter((f) => f.companyId === h.d.companyId).sort((a, b) => a.fiscalYear - b.fiscalYear).slice(-2);
                return (
                  <div key={h.d.id} className="mb-4">
                    <div className="text-sm font-semibold">{cName(h.d.companyId)}</div>
                    <p className="text-xs text-slate-600">설립 {p?.foundedOn?.replace(/-/g, '.') ?? '-'} · 대표 {p?.ceo ?? '-'} · 투자형태 {h.d.securityKind ?? '-'} · 투자 {M(h.inv)}</p>
                    {fin.length === 0 ? <p className="text-xs text-slate-500 mt-1">재무 자료 없음</p> : (
                      <T head={['구분', ...fin.map((f) => `${f.fiscalYear}.12`)]} right={fin.map((_, i) => i + 1)}
                        rows={[['자산총계', ...fin.map((f) => M(f.totalAssets ?? 0))], ['부채총계', ...fin.map((f) => M(f.totalLiabilities ?? 0))], ['자본총계', ...fin.map((f) => M(f.totalEquity ?? 0))],
                          ['매출액', ...fin.map((f) => M(f.revenue ?? 0))], ['영업이익', ...fin.map((f) => M(f.operatingIncome ?? 0))], ['당기순이익', ...fin.map((f) => M(f.netIncome ?? 0))]]} />
                    )}
                  </div>
                );
              })}
            </section>
          </article>
        </>
      )}
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
